/**
 * Live Delta scorecard payloads often omit innings (`innsN: null`) or send thinner objects
 * than the last frame. A straight replace clears batting/runs and leaves headers stuck at `0/0`.
 *
 * Thin frames also omit `bat_now` / `inns_now`. Dropping those flips home↔away when
 * `mapCricketInnsScoresToTeams` falls back to batting-order = fixture-order.
 */

/** HTTP initial-state used to nest the full WS message under `scorecard`; unwrap to inns*. */
export function normalizeIncomingScorecard(incoming: unknown): unknown {
  if (!incoming || typeof incoming !== "object" || Array.isArray(incoming)) return incoming;
  const o = incoming as Record<string, unknown>;
  if (o.inns1 != null || o.inns_now != null || o.bat_now != null) return o;
  if (o.scorecard && typeof o.scorecard === "object") return normalizeIncomingScorecard(o.scorecard);
  if (o.msg_data && typeof o.msg_data === "object") return normalizeIncomingScorecard(o.msg_data);
  return o;
}

function scorecardScalarPresent(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "number") return Number.isFinite(value);
  return true;
}

/** Keep orientation fields from the prior frame when a delta omits or nulls them. */
function retainScorecardScalar(
  out: Record<string, unknown>,
  prev: Record<string, unknown>,
  key: string,
): void {
  if (scorecardScalarPresent(out[key])) return;
  if (scorecardScalarPresent(prev[key])) out[key] = prev[key];
}

export function mergeDecimalScorecardDelta(prev: unknown, incoming: unknown): unknown {
  const normalized = normalizeIncomingScorecard(incoming);
  if (!normalized || typeof normalized !== "object") return prev ?? incoming;
  if (!prev || typeof prev !== "object") return { ...(normalized as Record<string, unknown>) };
  const p = prev as Record<string, unknown>;
  const n = normalized as Record<string, unknown>;
  const out: Record<string, unknown> = { ...n };
  for (let i = 1; i <= 4; i++) {
    const k = `inns${i}`;
    if (n[k] == null && p[k] != null) {
      out[k] = p[k];
    } else if (
      n[k] != null &&
      typeof n[k] === "object" &&
      !Array.isArray(n[k]) &&
      p[k] != null &&
      typeof p[k] === "object" &&
      !Array.isArray(p[k])
    ) {
      out[k] = { ...(p[k] as object), ...(n[k] as object) };
    }
  }
  // Preserve batting side / innings index — missing values swap team score rows.
  retainScorecardScalar(out, p, "bat_now");
  retainScorecardScalar(out, p, "batNow");
  retainScorecardScalar(out, p, "inns_now");
  return out;
}
